/**
 * Public invoice preview — Phase 6 migration (Feb 2026).
 *
 * Mounted at `/invoice-preview/:invoiceNumber` OUTSIDE the protected
 * <Layout> in App.js so the CRM navbar / sidebar do NOT render into the
 * Playwright-generated PDF. The body fetches the invoice via the
 * authenticated GET /api/invoices/{invoiceNumber} endpoint and renders
 * the existing <InvoicePreview> component with `printMode={true}`,
 * which hides only the Back / Download buttons. All printable content
 * (header, items table, tax summary, bank details, footer) is unchanged.
 *
 * Auth: the underlying GET endpoint requires auth (same as the itinerary
 * pattern). generatePDF() forwards httpOnly cookies + the localStorage
 * Bearer token to /api/pdf/render, which the headless Chromium replays
 * before navigating here — same proven plumbing as the voucher + itinerary
 * migrations.
 *
 * This route is intentionally NOT linked from any nav/menu. It exists
 * solely for the Playwright PDF render of an invoice.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import InvoicePreview from './InvoicePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoice() {
  const { invoiceNumber } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);

  // Opt this public/Playwright route out of the global @media print
  // visibility:hidden rule in index.css (added for the in-app voucher
  // print flow). Without this, page.pdf() blanked the entire body and
  // only the Playwright header/footer template survived.
  useEffect(() => {
    document.body.classList.add('pdf-public-route');
    return () => document.body.classList.remove('pdf-public-route');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/invoices/${encodeURIComponent(invoiceNumber)}`
        );
        if (!cancelled) {
          setInvoice(res.data);
          document.title = `${res.data?.invoiceNumber || 'Invoice'}`;
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load invoice');
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceNumber]);

  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!invoice) return <div style={{ padding: 24 }}>Loading…</div>;
  return <InvoicePreview invoice={invoice} printMode />;
}
